import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { PaperPlaneRight, MapPin, EnvelopeSimple, Phone, WhatsappLogo } from "@phosphor-icons/react";
import { useLang } from "../i18n/LangContext";
import { Magnetic } from "./Magnetic";
import { toast, Toaster } from "sonner";
import { IUBENDA } from "../constants/legal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const revealUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.9, ease: [0.7, 0, 0.2, 1] },
  }),
};

export const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    property_type: "",
    message: "",
  });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle");

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    if (!consent) {
      toast.error(t.contact.form.consentRequired || "Please accept the privacy policy to proceed.");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, { ...form, locale: lang, consent: true });
      setStatus("success");
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", phone: "", property_type: "", message: "" });
      setConsent(false);
      setTimeout(() => setStatus("idle"), 4000);
    } catch (err) {
      setStatus("error");
      toast.error(t.contact.form.error);
      setTimeout(() => setStatus("idle"), 4000);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#232326]/20 focus:border-[#8A6F45] outline-none py-4 text-[#232326] font-body text-base transition-colors";

  return (
    <section
      id="contact"
      className="relative bg-[#F1EEE8] text-[#232326] py-28 md:py-40"
      data-testid="contact-section"
    >
      <Toaster position="top-right" theme="dark" />
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="md:col-span-3">
            <div className="flex items-center gap-3 text-[#13535A] text-[0.7rem] tracking-[0.28em] uppercase font-semibold">
              <span className="w-6 h-px bg-[#8A6F45]" />
              <span>{t.contact.overline}</span>
            </div>
          </div>
          <div className="md:col-span-9">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={revealUp}
              className="font-display font-medium leading-[1.02] tracking-tight text-[clamp(2.8rem,7.5vw,7.5rem)] mb-8"
            >
              {t.contact.title}
            </motion.h2>
            <p className="text-[#232326]/70 text-lg max-w-xl leading-relaxed">
              {t.contact.subtitle}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-12 md:gap-16">
          <form
            onSubmit={submit}
            className="md:col-span-8 space-y-2"
            data-testid="contact-form"
          >
            <div className="grid md:grid-cols-2 gap-x-8">
              <div className="py-2">
                <label className="text-[0.68rem] tracking-[0.28em] uppercase text-[#232326]/45 block mb-2 font-medium">
                  {t.contact.form.name}
                </label>
                <input
                  required
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  className={inputCls}
                  data-testid="contact-input-name"
                />
              </div>
              <div className="py-2">
                <label className="text-[0.68rem] tracking-[0.28em] uppercase text-[#232326]/45 block mb-2 font-medium">
                  {t.contact.form.email}
                </label>
                <input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  className={inputCls}
                  data-testid="contact-input-email"
                />
              </div>
              <div className="py-2">
                <label className="text-[0.68rem] tracking-[0.28em] uppercase text-[#232326]/45 block mb-2 font-medium">
                  {t.contact.form.phone}
                </label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  className={inputCls}
                  data-testid="contact-input-phone"
                />
              </div>
              <div className="py-2">
                <label className="text-[0.68rem] tracking-[0.28em] uppercase text-[#232326]/45 block mb-2 font-medium">
                  {t.contact.form.type}
                </label>
                <select
                  name="property_type"
                  value={form.property_type}
                  onChange={onChange}
                  className={`${inputCls} appearance-none cursor-pointer`}
                  data-testid="contact-input-type"
                >
                  <option value="" className="bg-[#F1EEE8]">—</option>
                  {t.contact.form.options.map((opt) => (
                    <option key={opt} value={opt} className="bg-[#F1EEE8]">
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="py-2 pt-6">
              <label className="text-[0.68rem] tracking-[0.28em] uppercase text-[#232326]/45 block mb-2 font-medium">
                {t.contact.form.message}
              </label>
              <textarea
                required
                name="message"
                value={form.message}
                onChange={onChange}
                rows={4}
                className={`${inputCls} resize-none`}
                data-testid="contact-input-message"
              />
            </div>

            <div className="pt-6">
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="contact-consent-label">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#13535A] cursor-pointer flex-shrink-0"
                  data-testid="contact-consent"
                />
                <span className="text-[0.78rem] leading-relaxed text-[#232326]/70 group-hover:text-[#232326]/90 transition-colors">
                  {t.contact.form.consentText || (
                    <>
                      Ho letto e accetto l&apos;{" "}
                      <a
                        href={IUBENDA.privacyUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="underline decoration-[#13535A]/40 underline-offset-2 hover:decoration-[#13535A] text-[#13535A]"
                      >
                        Informativa Privacy (GDPR)
                      </a>{" "}
                      e acconsento al trattamento dei miei dati personali per essere ricontattato da Zitro.
                    </>
                  )}
                </span>
              </label>
            </div>

            <div className="pt-6">
              <Magnetic strength={0.25}>
                <button
                  type="submit"
                  disabled={status === "sending" || !consent}
                  className="btn-primary-inverse disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {status === "sending" ? t.contact.form.sending : t.contact.form.submit}
                  <PaperPlaneRight size={14} weight="light" />
                </button>
              </Magnetic>
            </div>
          </form>

          <aside className="md:col-span-4 space-y-10 border-l border-[#232326]/12 md:pl-12">
            <div>
              <div className="text-[0.68rem] tracking-[0.28em] uppercase text-[#232326]/45 mb-5 font-medium">
                {t.contact.infoTitle}
              </div>
              <div className="space-y-5 text-[#232326]/85">
                <div className="flex items-start gap-3">
                  <MapPin size={18} weight="light" className="text-[#8A6F45] mt-1 flex-shrink-0" />
                  <div className="font-body leading-relaxed whitespace-pre-line">{t.contact.infoAddress}</div>
                </div>
                <div className="flex items-start gap-3">
                  <EnvelopeSimple size={18} weight="light" className="text-[#8A6F45] mt-1 flex-shrink-0" />
                  <a href={`mailto:${t.contact.infoEmail}`} className="link-underline">
                    {t.contact.infoEmail}
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <Phone size={18} weight="light" className="text-[#8A6F45] mt-1 flex-shrink-0" />
                  <a href={`tel:${t.contact.infoPhone.replace(/\s/g,'')}`} className="link-underline">
                    {t.contact.infoPhone}
                  </a>
                </div>
              </div>
            </div>

            <div>
              <Magnetic strength={0.2}>
                <a
                  href={`https://wa.me/${t.contact.infoPhone.replace(/\D/g, "")}?text=${encodeURIComponent(t.contact.whatsappMsg)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-whatsapp"
                  className="btn-primary"
                >
                  <WhatsappLogo size={20} weight="fill" />
                  {t.contact.whatsapp}
                </a>
              </Magnetic>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};
